import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import SharedApiLogic from "../../api-client/shared-api-logic.ts";
import LogUtils from "../../utils/log-utils.ts";
import Restaurant from "../../models/restaurant.ts";
import StateUtils from "../../state/state-utils.ts";
import V1GroupRestaurantsChosen from "../../payloads/incoming/v1-group-restaurants-chosen.ts";
import CACHE_BUSTER from "../../../../../cache-buster.json" with { type: "json" };

export default class TopRestaurantsScreen extends BaseUI {
  private readonly shadowRealm: ShadowRoot;

  // DragEvent
  private draggableElementOriginal: HTMLDivElement | null;
  private draggableElementBeingHovered: HTMLDivElement | null;

  // TouchEvent
  private currentTouchEventIdentifier: number | null;
  private originalTouchEventTarget: HTMLDivElement | null;
  private touchEventTargetBeingHovered: HTMLDivElement | null;
  private readonly draggingActionIcon: HTMLImageElement;

  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
    shadowRealm: ShadowRoot,
  ) {
    super("top-restaurants-screen", state, apiClient, navigateCallback);

    // this is only used for the mobile Drag and Drop functionality via the TouchEvent API
    this.shadowRealm = shadowRealm;

    // subscribe to events
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
    this.apiClient.v1GroupRestaurantsChosenCallback = this.handleV1GroupRestaurantsChosen.bind(this);

    // keep track of the original element that started the drag event
    this.draggableElementOriginal = null;

    // keep track of which element is currently being hovered over when dragging
    this.draggableElementBeingHovered = null;

    // keep track of the current TouchEvent.identifier
    this.currentTouchEventIdentifier = null;

    // keep track of the original element that started the touch event
    this.originalTouchEventTarget = null;

    // keep track of which element is currently being hovered over when touching
    this.touchEventTargetBeingHovered = null;

    // create a fake draggable element for mobile Drag and Drop functionality
    this.draggingActionIcon = UiUtils.image(CACHE_BUSTER["static/image/icon/crown.webp"]);
    this.draggingActionIcon.id = "dragging-action-icon";
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // explain what the rules are
    const explanationParagraph1: HTMLParagraphElement = UiUtils.paragraph(
      "Rank the mystery restaurants by dragging and dropping them in a different order!",
    );
    newElements.push(explanationParagraph1);
    const explanationParagraph2: HTMLParagraphElement = UiUtils.paragraph(
      "Top is your most-favorite, bottom is your least-favorite.",
    );
    newElements.push(explanationParagraph2);

    // create container for the list of categories
    const topRestaurantsDiv: HTMLDivElement = UiUtils.div();
    topRestaurantsDiv.classList.add("top-restaurants");
    newElements.push(topRestaurantsDiv);

    // get the current group's Top Restaurants
    const topRestaurants: Restaurant[] = StateUtils.getCurrentGroupTopRestaurants(this.state);

    // display all the top restaurants
    topRestaurants.forEach((restaurant: Restaurant, index: number): void => {
      topRestaurantsDiv.appendChild(this.generateDraggableRestaurantDiv(restaurant, index));
    });

    // create submit button
    const submitButton: HTMLButtonElement = UiUtils.button(
      "Submit",
      this.handleSubmitRestaurantCategoryPreferences.bind(this),
    );
    newElements.push(submitButton);

    // style the fake draggable element and add it to the screen
    newElements.push(this.draggingActionIcon);

    return super.render(newElements);
  }

  private generateDraggableRestaurantDiv(restaurant: Restaurant, index: number): HTMLDivElement {
    // create draggable container for anonymous stats
    const draggableRestaurantDiv: HTMLDivElement = UiUtils.div();
    draggableRestaurantDiv.classList.add("draggable-top-restaurant");
    draggableRestaurantDiv.draggable = true;

    // listen for Drag events (desktop)
    draggableRestaurantDiv.addEventListener("dragstart", (dragEvent: DragEvent) => this.handleDragStart(dragEvent));
    draggableRestaurantDiv.addEventListener("dragend", (dragEvent: DragEvent) => this.handleDragEnd(dragEvent));
    draggableRestaurantDiv.addEventListener("dragover", (dragEvent: DragEvent) => this.handleDragOver(dragEvent));
    draggableRestaurantDiv.addEventListener("dragenter", (dragEvent: DragEvent) => this.handleDragEnter(dragEvent));
    draggableRestaurantDiv.addEventListener("dragleave", (dragEvent: DragEvent) => this.handleDragLeave(dragEvent));
    draggableRestaurantDiv.addEventListener("drop", (dragEvent: DragEvent) => this.handleDragDrop(dragEvent));

    // listen for Touch events (mobile)
    draggableRestaurantDiv.addEventListener(
      "touchstart",
      (touchEvent: TouchEvent) => this.handleTouchStart(touchEvent),
      {
        passive: false,
      },
    );
    draggableRestaurantDiv.addEventListener(
      "touchend",
      (touchEvent: TouchEvent) => this.handleTouchEnd(touchEvent),
      {
        passive: false,
      },
    );
    draggableRestaurantDiv.addEventListener(
      "touchcancel",
      (touchEvent: TouchEvent) => this.handleTouchCancel(touchEvent),
      {
        passive: false,
      },
    );
    draggableRestaurantDiv.addEventListener(
      "touchmove",
      (touchEvent: TouchEvent) => this.handleTouchMove(touchEvent),
      {
        passive: false,
      },
    );

    // anonymous stats
    const anonymousNameHeading: HTMLHeadingElement = UiUtils.heading(4, `Mystery Restaurant ${index + 1}`);
    anonymousNameHeading.dataset.id = restaurant.id;
    draggableRestaurantDiv.appendChild(anonymousNameHeading);
    draggableRestaurantDiv.appendChild(UiUtils.paragraph(`Rating: ${restaurant.rating}`));
    draggableRestaurantDiv.appendChild(UiUtils.paragraph(`Reviews: ${restaurant.reviewCount}`));
    draggableRestaurantDiv.appendChild(UiUtils.paragraph(`Price: ${restaurant.price}`));

    // distance (convert meters to miles)
    const metersPerMile = 1609.34;
    const distanceMiles: number = restaurant.distanceMeters / metersPerMile;
    const truncatedDistanceMiles: number = Math.trunc(distanceMiles * 100) / 100;
    draggableRestaurantDiv.appendChild(UiUtils.paragraph(`Distance: ${truncatedDistanceMiles}mi`));

    // finished
    return draggableRestaurantDiv;
  }

  private handleSubmitRestaurantCategoryPreferences(): void {
    // TODO - implement actual logic
    const restaurantPreferences: string[] = [];
    const topRestaurants: Restaurant[] = StateUtils.getCurrentGroupTopRestaurants(this.state);
    for (const topRestaurant of topRestaurants) {
      restaurantPreferences.push(topRestaurant.id);
    }

    // send payload
    this.apiClient.sendRestaurantPreferencesPayload(restaurantPreferences);
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    SharedApiLogic.handleV1GroupJoined(this.state, v1GroupJoined);
  }

  private handleV1GroupRestaurantsChosen(v1GroupRestaurantsChosen: V1GroupRestaurantsChosen): void {
    SharedApiLogic.handleV1GroupRestaurantsChosenCallback(this.debug, this.state, v1GroupRestaurantsChosen);

    // only change screen if the user that chose these Restaurant Preferences matches this client
    if (v1GroupRestaurantsChosen.payload.user_id === this.state.userState.id) {
      LogUtils.debug(this.debug, "Successfully submitted restaurant preferences.");

      // now that we've sent our choices, wait for everyone else to finish choosing
      this.navigateCallback(Route.TOP_RESTAURANTS_LOBBY);
    }
  }

  private handleDragStart(dragEvent: DragEvent): void {
    if (this.currentTouchEventIdentifier !== null) {
      LogUtils.warn(this.debug, "Attempted to handle a DragEvent when we're already handling a TouchEvent.");
      return;
    }

    // enforce target is draggable Div
    const eventTarget: EventTarget | null = dragEvent.target;
    if (eventTarget === null) {
      LogUtils.error("draggable element was unexpectedly null");
    }
    if (!(eventTarget instanceof HTMLDivElement)) {
      LogUtils.error(
        `attempted to drag element, but the event's target was a ${typeof eventTarget} when we expected an HTMLDivElement`,
      );
    }

    // save the fact that this is the original element that started the drag event
    this.draggableElementOriginal = eventTarget;
    this.draggableElementBeingHovered = null;

    // lower opacity to show that the Div is being moved
    eventTarget.style.opacity = "0.4";

    // specify the contents that will be dragged
    if (dragEvent.dataTransfer === null) {
      LogUtils.error("the DragEvent's 'dataTransfer' was unexpectedly null");
    }
    dragEvent.dataTransfer.effectAllowed = "move";
  }

  private handleDragEnd(dragEvent: DragEvent): void {
    // enforce target is draggable Div
    const eventTarget: EventTarget | null = dragEvent.target;
    if (eventTarget === null) {
      LogUtils.error("Draggable element was unexpectedly null");
    }
    if (!(eventTarget instanceof HTMLDivElement)) {
      LogUtils.error(
        `attempted to drag element, but the event's target was a ${typeof eventTarget} when we expected an HTMLDivElement`,
      );
    }

    // reset opacity to show Div is NOT being moved anymore
    eventTarget.style.opacity = "1";

    // reset elements we were tracking
    this.draggableElementOriginal = null;
    this.draggableElementBeingHovered = null;
  }

  private handleDragOver(event: DragEvent): boolean {
    event.preventDefault();
    return false;
  }

  private handleDragEnter(dragEvent: DragEvent): void {
    // enforce target is draggable Div
    const eventTarget: EventTarget | null = dragEvent.target;
    if (eventTarget === null) {
      LogUtils.error("Draggable element was unexpectedly null");
    }
    if (!(eventTarget instanceof HTMLDivElement)) {
      LogUtils.error("Draggable element was unexpectedly not a Div element.");
    }

    // if we're hovering over the original element, exit early
    if (eventTarget === this.draggableElementOriginal) {
      return;
    }

    // save the fact that this div is the one being hovered
    this.draggableElementBeingHovered = eventTarget;

    // add the drag-hover effect
    eventTarget.classList.add("drag-hover");
  }

  private handleDragLeave(dragEvent: DragEvent): void {
    // enforce target is draggable Div
    const eventTarget: EventTarget | null = dragEvent.target;
    if (eventTarget === null) {
      LogUtils.error("Draggable element was unexpectedly null");
    }
    if (!(eventTarget instanceof HTMLDivElement)) {
      LogUtils.error("Draggable element was unexpectedly not a Div element.");
    }

    // if we're hovering over the original element, exit early
    if (eventTarget === this.draggableElementOriginal) {
      return;
    }

    // save the fact that we are no longer hovering this div
    this.draggableElementBeingHovered = null;

    // remove the drag-hover effect
    eventTarget.classList.remove("drag-hover");
  }

  private handleDragDrop(dragEvent: DragEvent): void {
    dragEvent.stopPropagation();

    // enforce target is draggable Div
    const eventTarget: EventTarget | null = dragEvent.target;
    if (eventTarget === null) {
      LogUtils.error("Draggable element was unexpectedly null");
    }
    if (!(eventTarget instanceof HTMLDivElement)) {
      LogUtils.error("Draggable element was unexpectedly not a Div element.");
    }

    // transfer HTML from dragged element to the landing zone
    if (this.draggableElementOriginal === null) {
      LogUtils.error("the original draggable element was unexpectedly null");
    }
    if (this.draggableElementBeingHovered === null) {
      LogUtils.error("the draggable element being hovered was unexpectedly null");
    }
    const temporaryHtmlStorage: string = this.draggableElementBeingHovered.innerHTML;
    this.draggableElementBeingHovered.innerHTML = this.draggableElementOriginal.innerHTML;
    this.draggableElementOriginal.innerHTML = temporaryHtmlStorage;

    // remove the drag-hover effect from the element that was previously being hovered
    this.draggableElementBeingHovered.classList.remove("drag-hover");
  }

  private handleTouchStart(touchEvent: TouchEvent): void {
    touchEvent.preventDefault();

    // make sure we're not already handling a TouchEvent
    if (this.currentTouchEventIdentifier !== null) {
      LogUtils.warn(this.debug, "Attempted to handle a TouchEvent when we were already handling one.");
      return;
    }

    // make sure we aren't already handling a DragEvent
    if (this.draggableElementOriginal !== null) {
      LogUtils.warn(this.debug, "Attempted to handle a TouchEvent when we were already handling a DragEvent.");
      return;
    }

    // make sure there is at least one Touch
    if (touchEvent.changedTouches.length < 1) {
      LogUtils.warn(this.debug, "Attempted to handle a TouchEvent that had no touches.");
      return;
    }

    // get the first Touch (even if it is at random) and save its identifier
    // (to ensure we only handle this specific TouchEvent in the other handlers)
    const touch: Touch = touchEvent.changedTouches[0];
    this.currentTouchEventIdentifier = touch.identifier;

    // get the element that is under the Touch spot
    const element: Element | null = this.shadowRealm.elementFromPoint(touch.clientX, touch.clientY);
    if (element === null) {
      LogUtils.error("The element that received the TouchEvent was unexpectedly null.");
    }
    if (!(element instanceof HTMLDivElement)) {
      LogUtils.error("The element that received the TouchEvent was unexpectedly not a Div element.");
    }
    if (element.draggable === false) {
      LogUtils.error("The element that received the TouchEvent was unexpectedly not draggable.");
    }
    this.originalTouchEventTarget = element;

    // modify the opacity of the original element to pretend like it isn't there
    this.originalTouchEventTarget.style.opacity = "0.4";

    // use the fake draggable element to track the TouchEvent
    this.draggingActionIcon.style.display = "unset";
    this.draggingActionIcon.style.left = `${touch.clientX}px`;
    this.draggingActionIcon.style.top = `${touch.clientY}px`;
  }

  private handleTouchMove(touchEvent: TouchEvent): void {
    touchEvent.preventDefault();

    // find the Touch that we are tracking
    let touch: Touch | null = null;
    for (const changedTouch of touchEvent.changedTouches) {
      if (changedTouch.identifier === this.currentTouchEventIdentifier) {
        touch = changedTouch;
        break;
      }
    }

    // make sure we found the Touch that we were tracking
    // (it's possible that this TouchEvent is for a different Touch)
    if (touch === null) {
      LogUtils.warn(this.debug, "Could not find the Touch that we were tracking.");
      return;
    }

    // move the element to the cursor's position
    this.draggingActionIcon.style.left = `${touch.clientX}px`;
    this.draggingActionIcon.style.top = `${touch.clientY}px`;

    // get the element that is under the Touch spot
    const element: Element | null = this.shadowRealm.elementFromPoint(touch.clientX, touch.clientY);
    if (element === null) {
      LogUtils.error("The element under the TouchEvent was unexpectedly null.");
    }

    // we only care about draggable Div elements that are not the original element
    if (
      !(element instanceof HTMLDivElement) || element.draggable === false || element === this.originalTouchEventTarget
    ) {
      if (this.touchEventTargetBeingHovered !== null) {
        // remove the drag-hover effect
        this.touchEventTargetBeingHovered.classList.remove("drag-hover");

        // save the fact that we are no longer hovering this div
        this.touchEventTargetBeingHovered = null;
      }
      return;
    }

    // if we already know that we're hovering over this element, exit early
    if (element === this.touchEventTargetBeingHovered) {
      return;
    }

    // track the element that we're hovering over
    this.touchEventTargetBeingHovered = element;

    // add the drag-hover effect
    element.classList.add("drag-hover");
  }

  private handleTouchEnd(touchEvent: TouchEvent): void {
    touchEvent.preventDefault();

    // transfer HTML from dragged element to the landing zone
    if (this.originalTouchEventTarget === null) {
      LogUtils.error("The original TouchEvent target was unexpectedly null.");
    }
    if (this.touchEventTargetBeingHovered !== null) {
      const temporaryHtmlStorage: string = this.touchEventTargetBeingHovered.innerHTML;
      this.touchEventTargetBeingHovered.innerHTML = this.originalTouchEventTarget.innerHTML;
      this.originalTouchEventTarget.innerHTML = temporaryHtmlStorage;

      // remove the drag-hover effect from the element that was previously being hovered
      this.touchEventTargetBeingHovered.classList.remove("drag-hover");
    }

    // reset the opacity of the original element
    this.originalTouchEventTarget.style.opacity = "1";

    // hide the fake draggable element
    this.draggingActionIcon.style.display = "none";

    // reset the current TouchEvent identifier, the original TouchEvent target, and the element being hovered
    this.currentTouchEventIdentifier = null;
    this.originalTouchEventTarget = null;
    this.touchEventTargetBeingHovered = null;
  }

  private handleTouchCancel(touchEvent: TouchEvent): void {
    touchEvent.preventDefault();

    // transfer HTML from dragged element to the landing zone
    if (this.originalTouchEventTarget === null) {
      LogUtils.error("The original TouchEvent target was unexpectedly null.");
    }
    if (this.touchEventTargetBeingHovered !== null) {
      const temporaryHtmlStorage: string = this.touchEventTargetBeingHovered.innerHTML;
      this.touchEventTargetBeingHovered.innerHTML = this.originalTouchEventTarget.innerHTML;
      this.originalTouchEventTarget.innerHTML = temporaryHtmlStorage;

      // remove the drag-hover effect from the element that was previously being hovered
      this.touchEventTargetBeingHovered.classList.remove("drag-hover");
    }

    // reset the opacity of the original element
    this.originalTouchEventTarget.style.opacity = "1";

    // hide the fake draggable element
    this.draggingActionIcon.style.display = "none";

    // reset the current TouchEvent identifier, the original TouchEvent target, and the element being hovered
    this.currentTouchEventIdentifier = null;
    this.originalTouchEventTarget = null;
    this.touchEventTargetBeingHovered = null;
  }
}
